import React, { useState } from "react";
import { createPortal } from "react-dom";
import schedulerService from "../../../services/scheduler";
import Icon from "./icons";
import css from "./Scheduler.module.css";

// Full 24-hour clock in 30-minute steps: 12:00 AM, 12:30 AM, ... 11:30 PM.
const TIME_OPTIONS = (() => {
  const out = [];
  for (let m = 0; m < 24 * 60; m += 30) {
    const h = Math.floor(m / 60);
    const min = m % 60;
    const mer = h < 12 ? "AM" : "PM";
    const h12 = h % 12 === 0 ? 12 : h % 12;
    out.push(`${h12}:${String(min).padStart(2, "0")} ${mer}`);
  }
  return out;
})();

// "7:00 AM" / "11:59 PM" -> minutes from midnight (model stores dayStartMin/dayEndMin as Number).
const labelToMin = (label) => {
  const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(label || "");
  if (!m) return 0;
  let h = Number(m[1]) % 12;
  if (/PM/i.test(m[3])) h += 12;
  return h * 60 + Number(m[2]);
};

const hhmmToMin = (hhmm) => {
  const [h, m] = (hhmm || "0:0").split(":").map(Number);
  return h * 60 + m;
};

// minutes -> "9:00 AM" (wraps past midnight for overnight templates).
const minLabel = (m) => {
  const mm = ((m % 1440) + 1440) % 1440;
  const h = Math.floor(mm / 60);
  const mn = mm % 60;
  const mer = h < 12 ? "AM" : "PM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(mn).padStart(2, "0")} ${mer}`;
};
const tplRange = (a, b) => `${minLabel(a)} – ${minLabel(b)} · ${Math.round((b - a) / 60)}h`;

// label -> hours number (0 = no limit), matching schedulerSettings.rules.maxShiftLength (Number).
const MAX_LEN_OPTS = [
  { label: "8 hours", value: 8 },
  { label: "10 hours", value: 10 },
  { label: "12 hours", value: 12 },
  { label: "No limit", value: 0 },
];

// The five default shift templates from the approved prototype. First four on by default.
const DEFAULT_TEMPLATES = [
  { id: "morning", name: "Morning", startMin: 7 * 60, endMin: 15 * 60 },
  { id: "day", name: "Day", startMin: 9 * 60, endMin: 17 * 60 },
  { id: "evening", name: "Evening", startMin: 15 * 60, endMin: 23 * 60 },
  { id: "overnight", name: "Overnight", startMin: 23 * 60, endMin: 31 * 60 },
  { id: "half", name: "Half Day", startMin: 9 * 60, endMin: 13 * 60 },
];
const DEFAULT_ON = ["morning", "day", "evening", "overnight"];

const STEPS = [
  { label: "Operating hours", desc: "When facilities are open", icon: "clock", tile: "teal" },
  { label: "Shift templates", desc: "Reusable shift blocks", icon: "calendar", tile: "violet" },
  { label: "Scheduling rules", desc: "Guardrails & limits", icon: "shield", tile: "info" },
  { label: "Reminders", desc: "Notify staff of shifts", icon: "bell", tile: "amber" },
  { label: "Review", desc: "Confirm & finish", icon: "check", tile: "violet" },
];

function Toggle({ on, onClick }) {
  return <button type="button" className={`${css.twSwitch} ${on ? css.on : ""}`} onClick={onClick} aria-pressed={on} />;
}

const HOURS_DEFAULT = { open24: false, dayStart: "7:00 AM", dayEnd: "8:00 PM" };

// The three operating-hours rows (Open 24h / Opens / Closes), reused by the
// "all facilities" card and each per-facility card.
function HoursRows({ cfg, onChange }) {
  return (
    <>
      <div className={css.swToggleRow}>
        <div>
          <div className={css.swRt}>Open 24 hours</div>
          <div className={css.swRs}>Operate around the clock.</div>
        </div>
        <Toggle on={cfg.open24} onClick={() => onChange("open24", !cfg.open24)} />
      </div>
      <div className={css.swToggleRow}>
        <div className={css.swRt}>Opens</div>
        <select className={css.sel} value={cfg.dayStart} disabled={cfg.open24} onChange={(e) => onChange("dayStart", e.target.value)}>
          {TIME_OPTIONS.map((h) => (
            <option key={h}>{h}</option>
          ))}
        </select>
      </div>
      <div className={css.swToggleRow}>
        <div className={css.swRt}>Closes</div>
        <select className={css.sel} value={cfg.dayEnd} disabled={cfg.open24} onChange={(e) => onChange("dayEnd", e.target.value)}>
          {TIME_OPTIONS.map((h) => (
            <option key={h}>{h}</option>
          ))}
        </select>
      </div>
    </>
  );
}

export default function SetupWizard({ opened, onClose, onComplete, facilities = [] }) {
  const [active, setActive] = useState(0);
  const [busy, setBusy] = useState(false);

  // Operating hours — per-facility, with a "same for all" toggle (matches prototype).
  const [hoursSameAll, setHoursSameAll] = useState(true);
  const [allHours, setAllHours] = useState(HOURS_DEFAULT);
  const [facHours, setFacHours] = useState({});
  const [openHoursFac, setOpenHoursFac] = useState(null);
  const weekStartsOn = "Sunday"; // configured in Settings, not the wizard

  // shift templates step
  const [templates, setTemplates] = useState(DEFAULT_TEMPLATES);
  const [tplSameAll, setTplSameAll] = useState(true);
  const [allTpl, setAllTpl] = useState(() => Object.fromEntries(DEFAULT_TEMPLATES.map((t) => [t.id, DEFAULT_ON.includes(t.id)])));
  const [facTpl, setFacTpl] = useState({}); // { [facilityId]: { [tplId]: bool } }
  const [openFac, setOpenFac] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newStart, setNewStart] = useState("09:00");
  const [newEnd, setNewEnd] = useState("17:00");

  const [rules, setRules] = useState({
    warnDoubleBooking: true,
    overtimeAlerts: true,
    maxShiftLength: 12,
    restrictClockInToScheduled: true,
  });

  const [reminders, setReminders] = useState({ enabled: true, h24: true, h1: true });

  if (!opened) return null;

  const rule = (k) => () => setRules((r) => ({ ...r, [k]: !r[k] }));

  const facHoursOf = (facId) => facHours[facId] || HOURS_DEFAULT;
  const setFacHoursVal = (facId, key, val) =>
    setFacHours((p) => ({ ...p, [facId]: { ...(p[facId] || HOURS_DEFAULT), [key]: val } }));
  const buildFacilityHours = () => {
    if (hoursSameAll) {
      return [{ open24: allHours.open24, dayStartMin: labelToMin(allHours.dayStart), dayEndMin: labelToMin(allHours.dayEnd) }];
    }
    return facilities.map((f) => {
      const c = facHoursOf(f.value);
      return { facility: f.value, open24: c.open24, dayStartMin: labelToMin(c.dayStart), dayEndMin: labelToMin(c.dayEnd) };
    });
  };

  const facCfgOf = (facId) => facTpl[facId] || Object.fromEntries(templates.map((t) => [t.id, DEFAULT_ON.includes(t.id)]));
  const setFacTplVal = (facId, tid, val) =>
    setFacTpl((prev) => ({ ...prev, [facId]: { ...(prev[facId] || Object.fromEntries(templates.map((t) => [t.id, DEFAULT_ON.includes(t.id)]))), [tid]: val } }));

  const addCustom = () => {
    const nm = newName.trim();
    if (!nm) return;
    const s = hhmmToMin(newStart);
    let e = hhmmToMin(newEnd);
    if (e <= s) e += 1440;
    const id = `c${Date.now()}`;
    setTemplates((prev) => [...prev, { id, name: nm, startMin: s, endMin: e }]);
    setAllTpl((prev) => ({ ...prev, [id]: true }));
    // A new shift applies to every facility by default (toggle off per card).
    setFacTpl((prev) => {
      const next = {};
      facilities.forEach((f) => {
        const base = prev[f.value] || Object.fromEntries(templates.map((t) => [t.id, DEFAULT_ON.includes(t.id)]));
        next[f.value] = { ...base, [id]: true };
      });
      return next;
    });
    setAddOpen(false);
    setNewName("");
    setNewStart("09:00");
    setNewEnd("17:00");
  };

  const buildTemplates = () => {
    const out = [];
    if (tplSameAll) {
      templates.forEach((t) => {
        if (allTpl[t.id]) out.push({ name: t.name, startMin: t.startMin, endMin: t.endMin, facility: null });
      });
    } else {
      facilities.forEach((f) => {
        const cfg = facCfgOf(f.value);
        templates.forEach((t) => {
          if (cfg[t.id]) out.push({ name: t.name, startMin: t.startMin, endMin: t.endMin, facility: f.value });
        });
      });
    }
    return out;
  };

  const templateCount = buildTemplates().length;

  const finish = async () => {
    setBusy(true);
    const leadTimes = [];
    if (reminders.enabled && reminders.h24) leadTimes.push("24h");
    if (reminders.enabled && reminders.h1) leadTimes.push("1h");
    await schedulerService.seedTemplates(buildTemplates());
    await schedulerService.updateSchedulerSettings({
      weekStartsOn,
      facilityHours: buildFacilityHours(),
      rules,
      reminders: { enabled: reminders.enabled, leadTimes },
      setupCompleted: true,
    });
    setBusy(false);
    onComplete?.();
  };

  const next = () => setActive((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setActive((s) => Math.max(s - 1, 0));
  const isLast = active === STEPS.length - 1;
  const step = STEPS[active];

  return createPortal(
    <div className={css.swWrap}>
      {/* Left rail */}
      <aside className={css.swRail}>
        <div className={css.swRailBrand}>
          <span className={css.swRailRocket}>
            <Icon name="gear" size={28} />
          </span>
        </div>
        <div className={css.swRailEyebrow}>Scheduler setup</div>
        <div className={css.swRailTitle}>Get your Scheduler ready</div>
        <div className={css.swRailSub}>A few quick choices and you can start building schedules.</div>
        <div className={css.swRailDivider} />
        <div className={css.swVsteps}>
          {STEPS.map((st, i) => {
            const done = i < active;
            const cls = `${css.swVstep} ${i === active ? css.active : done ? css.done : ""} ${i <= active ? css.clickable : ""}`;
            return (
              <div key={st.label} className={cls} onClick={() => i <= active && setActive(i)}>
                <span className={css.vsDot}>
                  <Icon name={done ? "check" : st.icon} size={16} />
                </span>
                <div className={css.vsTxt}>
                  <div className={css.vsLbl}>{st.label}</div>
                  <div className={css.vsSub}>{st.desc}</div>
                </div>
              </div>
            );
          })}
        </div>
        <div className={css.swRailScope}>
          <span>
            Applies to <b>all facilities</b>
          </span>
          <span style={{ fontSize: 11, color: "rgba(255,255,255,0.45)" }}>You can fine-tune anything later in Settings.</span>
        </div>
      </aside>

      {/* Main */}
      <section className={css.swMain}>
        <div className={css.swMaintop}>
          <span className={css.swStepcount}>
            Step {active + 1} of {STEPS.length}
          </span>
          <button type="button" className={css.swX} onClick={onClose} aria-label="Close">
            <Icon name="x" size={16} />
          </button>
        </div>

        <div className={css.swBody}>
          <div className={css.swInner}>
            {!isLast && (
              <div className={css.swIntro}>
                <span className={`${css.swItile} ${css[step.tile]}`}>
                  <Icon name={step.icon} size={24} />
                </span>
                <div>
                  <div className={css.swH2}>{step.label}</div>
                  <div className={css.swIsub}>
                    {active === 0 && "Set when your facilities operate. This frames the calendar and time windows."}
                    {active === 1 && "Reusable shift blocks you can drop onto the calendar. Pick which apply to each facility."}
                    {active === 2 && "Guardrails that keep schedules realistic. Recommended defaults are on."}
                    {active === 3 && "Automatically remind assigned staff before their shifts start."}
                  </div>
                </div>
              </div>
            )}

            {active === 0 && (
              <div className={css.swAnim}>
                <div className={css.swAllbar}>
                  <div>
                    <div className={css.swRt}>Same hours for every facility</div>
                    <div className={css.swRs}>
                      {hoursSameAll ? `Applies to all ${facilities.length || ""} facilities.` : "Set each facility below. Tap one to expand."}
                    </div>
                  </div>
                  <Toggle on={hoursSameAll} onClick={() => setHoursSameAll((v) => !v)} />
                </div>

                {hoursSameAll ? (
                  <div className={css.swCard}>
                    <HoursRows cfg={allHours} onChange={(k, v) => setAllHours((p) => ({ ...p, [k]: v }))} />
                  </div>
                ) : (
                  facilities.map((f) => {
                    const cfg = facHoursOf(f.value);
                    const isOpen = openHoursFac === f.value;
                    return (
                      <div key={f.value} className={`${css.swFcard} ${isOpen ? css.open : ""}`}>
                        <div className={css.swFhead} onClick={() => setOpenHoursFac(isOpen ? null : f.value)}>
                          <span className={css.swFic}><Icon name="hospital" size={15} /></span>
                          <span className={css.swFnm}>{f.label}</span>
                          <span className={css.swFsum}>{cfg.open24 ? "Open 24 hours" : `${cfg.dayStart} – ${cfg.dayEnd}`}</span>
                          <span className={css.swFchev}><Icon name="chevronDown" size={16} /></span>
                        </div>
                        {isOpen && (
                          <div className={css.swFbody}>
                            <HoursRows cfg={cfg} onChange={(k, v) => setFacHoursVal(f.value, k, v)} />
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {active === 1 && (
              <div className={css.swAnim}>
                <div className={css.swAllbar}>
                  <div>
                    <div className={css.swRt}>Same shifts for every facility</div>
                    <div className={css.swRs}>
                      {tplSameAll ? `Applies to all ${facilities.length || ""} facilities.` : "Set each facility below. Tap one to expand."}
                    </div>
                  </div>
                  <Toggle on={tplSameAll} onClick={() => setTplSameAll((v) => !v)} />
                </div>

                {addOpen ? (
                  <div className={css.swAddform} style={{ marginTop: 0, marginBottom: 12 }}>
                    <div className={css.swAfT}><Icon name="calendar" size={15} /> New shift template</div>
                    <div className={css.swAfNote}>This shift will be added to every facility. You can turn it off for individual facilities below.</div>
                    <div className={css.swField}>
                      <label>Shift name</label>
                      <input className={css.swInp} value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Night, Weekend AM" autoComplete="off" />
                    </div>
                    <div className={css.swFrow}>
                      <div className={css.swField} style={{ marginBottom: 0 }}>
                        <label>Starts</label>
                        <input className={css.swInp} type="time" value={newStart} onChange={(e) => setNewStart(e.target.value)} />
                      </div>
                      <div className={css.swField} style={{ marginBottom: 0 }}>
                        <label>Ends</label>
                        <input className={css.swInp} type="time" value={newEnd} onChange={(e) => setNewEnd(e.target.value)} />
                      </div>
                    </div>
                    <div className={css.swAfFoot}>
                      <button type="button" className={`${css.swBtnSm} ${css.swBtnGhost}`} onClick={() => setAddOpen(false)}>Cancel</button>
                      <button type="button" className={`${css.swBtnSm} ${css.swBtnPrimary}`} onClick={addCustom}><Icon name="plus" size={15} /> Add shift</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ marginBottom: 12 }}>
                    <button type="button" className={css.swAddbtn} style={{ marginTop: 0 }} onClick={() => setAddOpen(true)}>
                      <Icon name="plus" size={15} /> Add a shift for all facilities
                    </button>
                    <div className={css.swAfNote} style={{ textAlign: "center", marginTop: 6 }}>
                      New shifts apply to every facility — toggle off per facility below.
                    </div>
                  </div>
                )}

                {tplSameAll ? (
                  <div className={css.swCard}>
                    {templates.map((t) => (
                      <div key={t.id} className={css.swToggleRow}>
                        <div>
                          <div className={css.swRt}>{t.name}</div>
                          <div className={css.swRs}>{tplRange(t.startMin, t.endMin)}</div>
                        </div>
                        <Toggle on={!!allTpl[t.id]} onClick={() => setAllTpl((p) => ({ ...p, [t.id]: !p[t.id] }))} />
                      </div>
                    ))}
                  </div>
                ) : (
                  facilities.map((f) => {
                    const cfg = facCfgOf(f.value);
                    const isOpen = openFac === f.value;
                    const n = templates.filter((t) => cfg[t.id]).length;
                    return (
                      <div key={f.value} className={`${css.swFcard} ${isOpen ? css.open : ""}`}>
                        <div className={css.swFhead} onClick={() => setOpenFac(isOpen ? null : f.value)}>
                          <span className={css.swFic}><Icon name="hospital" size={15} /></span>
                          <span className={css.swFnm}>{f.label}</span>
                          <span className={css.swFsum}>{n} shift{n === 1 ? "" : "s"}</span>
                          <span className={css.swFchev}><Icon name="chevronDown" size={16} /></span>
                        </div>
                        {isOpen && (
                          <div className={css.swFbody}>
                            {templates.map((t) => (
                              <div key={t.id} className={css.swToggleRow}>
                                <div>
                                  <div className={css.swRt}>{t.name}</div>
                                  <div className={css.swRs}>{tplRange(t.startMin, t.endMin)}</div>
                                </div>
                                <Toggle on={!!cfg[t.id]} onClick={() => setFacTplVal(f.value, t.id, !cfg[t.id])} />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {active === 2 && (
              <div className={css.swAnim}>
                <div className={css.swCard}>
                  <div className={css.swToggleRow}>
                    <div>
                      <div className={css.swRt}>Warn on double-booking</div>
                      <div className={css.swRs}>Flag when a staff member is scheduled twice at once.</div>
                    </div>
                    <Toggle on={rules.warnDoubleBooking} onClick={rule("warnDoubleBooking")} />
                  </div>
                  <div className={css.swToggleRow}>
                    <div>
                      <div className={css.swRt}>Overtime alerts</div>
                      <div className={css.swRs}>Warn when weekly hours cross the overtime threshold.</div>
                    </div>
                    <Toggle on={rules.overtimeAlerts} onClick={rule("overtimeAlerts")} />
                  </div>
                  <div className={css.swToggleRow}>
                    <div>
                      <div className={css.swRt}>Maximum shift length</div>
                      <div className={css.swRs}>Block shifts longer than this.</div>
                    </div>
                    <select
                      className={css.sel}
                      value={rules.maxShiftLength}
                      onChange={(e) => setRules((r) => ({ ...r, maxShiftLength: Number(e.target.value) }))}
                    >
                      {MAX_LEN_OPTS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={css.swToggleRow}>
                    <div>
                      <div className={css.swRt}>Restrict clock-in to scheduled shifts</div>
                      <div className={css.swRs}>Staff can only clock in for a shift they are assigned to.</div>
                    </div>
                    <Toggle on={rules.restrictClockInToScheduled} onClick={rule("restrictClockInToScheduled")} />
                  </div>
                </div>
              </div>
            )}

            {active === 3 && (
              <div className={css.swAnim}>
                <div className={css.swTip}>
                  <Icon name="alert" size={18} />
                  <div className={css.swTt}>Reminders are sent as in-app notifications when you publish a schedule.</div>
                </div>
                <div className={css.swCard}>
                  <div className={css.swToggleRow}>
                    <div>
                      <div className={css.swRt}>Enable shift reminders</div>
                      <div className={css.swRs}>Notify assigned staff before their shifts.</div>
                    </div>
                    <Toggle on={reminders.enabled} onClick={() => setReminders((r) => ({ ...r, enabled: !r.enabled }))} />
                  </div>
                  <div className={css.swToggleRow}>
                    <div>
                      <div className={css.swRt}>24 hours before</div>
                      <div className={css.swRs}>A day-ahead heads up.</div>
                    </div>
                    <Toggle on={reminders.enabled && reminders.h24} onClick={() => reminders.enabled && setReminders((r) => ({ ...r, h24: !r.h24 }))} />
                  </div>
                  <div className={css.swToggleRow}>
                    <div>
                      <div className={css.swRt}>1 hour before</div>
                      <div className={css.swRs}>A final nudge before clock-in.</div>
                    </div>
                    <Toggle on={reminders.enabled && reminders.h1} onClick={() => reminders.enabled && setReminders((r) => ({ ...r, h1: !r.h1 }))} />
                  </div>
                </div>
              </div>
            )}

            {isLast && (
              <div className={`${css.swAnim} ${css.swDone}`}>
                <span className={css.swDoneBurst}>
                  <Icon name="check" size={46} />
                </span>
                <h1 className={css.swDoneH1}>Your Scheduler is ready</h1>
                <p className={css.swDoneP}>
                  Build staff schedules, add appointments, and track time and EVV across your facilities.
                </p>
                <div className={css.swSummary}>
                  <div className={css.swSum}>
                    <span className={css.swSumB}>{templateCount}</span>
                    <div className={css.swSumL}>Shift templates</div>
                  </div>
                  <div className={css.swSum}>
                    <span className={css.swSumB}>{rules.maxShiftLength === 0 ? "∞" : `${rules.maxShiftLength}h`}</span>
                    <div className={css.swSumL}>Max shift</div>
                  </div>
                  <div className={css.swSum}>
                    <span className={css.swSumB}>
                      {reminders.enabled ? [reminders.h24 && "24h", reminders.h1 && "1h"].filter(Boolean).length : 0}
                    </span>
                    <div className={css.swSumL}>Reminders</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className={css.swFoot}>
          {active > 0 ? (
            <button type="button" className={css.btnG} onClick={back}>
              Back
            </button>
          ) : (
            <span />
          )}
          <span style={{ flex: 1 }} />
          {!isLast ? (
            <button type="button" className={css.btnP} onClick={next}>
              Continue
            </button>
          ) : (
            <button type="button" className={`${css.btnP} ${css.dark}`} onClick={finish} disabled={busy}>
              {busy ? "Saving…" : "Finish setup"}
            </button>
          )}
        </div>
      </section>
    </div>,
    document.body
  );
}
